/**
 * Wishlist (customer "likes").
 *
 * - State comes from the JSON rendered by snippets/wishlist-data.liquid
 *   (customer metafield custom.wishlist_ids).
 * - A like is saved by POSTing to the wishlist app through the store's app
 *   proxy (/apps/wishlist/toggle). Shopify adds the signed ID of the logged-in
 *   customer to that request; the theme never sends a customer ID.
 * - <wishlist-button> elements (snippets/wishlist-button.liquid) show and
 *   change the state. Links marked [data-wishlist-link] go to the wishlist page.
 * - Logged-out visitors: a click on either opens the sign-in panel instead.
 */

/**
 * @typedef {object} WishlistConfig
 * @property {boolean} loggedIn
 * @property {string | null} customerId
 * @property {string[]} ids
 * @property {string} endpoint
 * @property {string} pageUrl
 * @property {string} loginUrl
 * @property {{ add: string, remove: string, error: string, limit: string }} strings
 */

const CHANGE_EVENT = 'wishlist:change';
const RECENT_KEY = 'wishlist:recent';

/**
 * How long the result of the last save is preferred over the server-rendered
 * list. Covers the short window in which a page rendered right after a save
 * can still show the previous metafield value.
 */
const RECENT_TTL_MS = 60 * 1000;

/** @returns {WishlistConfig} */
function readConfig() {
  /** @type {WishlistConfig} */
  const fallback = {
    loggedIn: false,
    customerId: null,
    ids: [],
    endpoint: '/apps/wishlist/toggle',
    pageUrl: '/pages/wishlist',
    loginUrl: '/account/login',
    strings: { add: 'Add to wishlist', remove: 'Remove from wishlist', error: '', limit: '' },
  };

  try {
    const parsed = JSON.parse(document.getElementById('wishlist-data')?.textContent || 'null');
    if (!parsed || typeof parsed !== 'object') return fallback;

    return {
      ...fallback,
      ...parsed,
      customerId: parsed.customerId == null ? null : String(parsed.customerId),
      ids: Array.isArray(parsed.ids) ? parsed.ids.map(String) : [],
      strings: { ...fallback.strings, ...(parsed.strings || {}) },
    };
  } catch {
    return fallback;
  }
}

const config = readConfig();

/** @returns {string[] | null} */
function readRecent() {
  try {
    const recent = JSON.parse(sessionStorage.getItem(RECENT_KEY) || 'null');
    if (!recent || recent.customerId !== config.customerId) return null;
    if (Date.now() - recent.savedAt > RECENT_TTL_MS) return null;
    return Array.isArray(recent.ids) ? recent.ids.map(String) : null;
  } catch {
    return null;
  }
}

/** @param {string[]} ids */
function writeRecent(ids) {
  try {
    sessionStorage.setItem(
      RECENT_KEY,
      JSON.stringify({ customerId: config.customerId, ids, savedAt: Date.now() })
    );
  } catch {
    // Storage can be unavailable (private mode, quota). The server-rendered list is used then.
  }
}

/** @type {Set<string>} */
const likedIds = new Set(config.loggedIn ? readRecent() ?? config.ids : []);

/**
 * Product IDs whose save is queued or in flight.
 * @type {Set<string>}
 */
const pending = new Set();

/**
 * Saves run one at a time, in click order. Each response carries the full
 * saved list; with parallel requests a slower, older response could overwrite
 * a newer one.
 * @type {Promise<void>}
 */
let saveQueue = Promise.resolve();

/** @param {string} productId */
function notify(productId) {
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { productId } }));
}

/**
 * Opens the same sign-in panel as the header account button. Falls back to
 * the login page when the account component is not on the page.
 */
function openLogin() {
  const account = document.querySelector('shopify-account');
  const trigger = account?.shadowRoot?.querySelector('button');

  if (trigger instanceof HTMLElement) {
    trigger.click();
    return;
  }
  window.location.assign(config.loginUrl);
}

/** @type {number | undefined} */
let toastTimer;

/**
 * The live region is added to the page before a message is needed, because
 * screen readers do not announce a region that is created already filled.
 * @returns {HTMLElement}
 */
function getToast() {
  let toast = document.querySelector('.wishlist-toast');
  if (!(toast instanceof HTMLElement)) {
    toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.hidden = true;
    document.body.appendChild(toast);
  }
  return /** @type {HTMLElement} */ (toast);
}

/** @param {string} message */
function showMessage(message) {
  if (!message) return;

  const toast = getToast();
  toast.textContent = message;
  toast.hidden = false;

  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => {
    toast.hidden = true;
    toast.textContent = '';
  }, 4000);
}

/**
 * Likes or unlikes a product. The buttons update immediately; the save is
 * queued and the change is undone if the save fails.
 *
 * @param {string} productId
 */
function toggle(productId) {
  if (!config.loggedIn) {
    openLogin();
    return;
  }
  if (pending.has(productId)) return;

  const like = !likedIds.has(productId);

  pending.add(productId);
  if (like) likedIds.add(productId);
  else likedIds.delete(productId);
  notify(productId);

  saveQueue = saveQueue
    .then(() => save(productId, like))
    .catch(() => {
      // Never leave a button busy or block later saves because of an unexpected error.
      pending.delete(productId);
      notify(productId);
    });
}

/**
 * @param {string} productId
 * @param {boolean} like
 */
async function save(productId, like) {
  /** @type {{ ok?: boolean, ids?: unknown, error?: string } | null} */
  let result = null;
  let status = 0;

  try {
    const response = await fetch(config.endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ productId, action: like ? 'add' : 'remove' }),
    });
    status = response.status;
    result = await response.json().catch(() => null);
  } catch {
    result = null;
  }

  pending.delete(productId);

  if (result?.ok && Array.isArray(result.ids)) {
    // The app returns the full saved list, so changes made in another tab are picked up too.
    const saved = result.ids.map(String);
    const next = new Set(saved);

    // Products still waiting in the queue keep the state the customer chose.
    pending.forEach((id) => {
      if (likedIds.has(id)) next.add(id);
      else next.delete(id);
    });

    const changed = new Set([...likedIds, ...next, productId]);
    likedIds.clear();
    next.forEach((id) => likedIds.add(id));

    // Only a list with nothing left to save matches what is stored.
    if (pending.size === 0) writeRecent(saved);
    changed.forEach(notify);
    return;
  }

  // Not saved: undo the optimistic change.
  if (like) likedIds.delete(productId);
  else likedIds.add(productId);
  notify(productId);

  if (status === 401 || result?.error === 'not_logged_in') {
    // The session ended since the page was rendered, so the header still shows
    // the signed-in account button. Go to the login page directly.
    window.location.assign(config.loginUrl);
    return;
  }
  showMessage(result?.error === 'limit_reached' ? config.strings.limit : config.strings.error);
}

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    document.addEventListener(CHANGE_EVENT, this.#onChange);
    this.#render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  get productId() {
    return this.dataset.productId || '';
  }

  /** @param {Event} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.wishlist-button__button')) return;

    // The button sits on product cards, which are links.
    event.preventDefault();
    if (this.productId) toggle(this.productId);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    if (event instanceof CustomEvent && event.detail?.productId === this.productId) this.#render();
  };

  #render() {
    const button = this.querySelector('.wishlist-button__button');
    if (!(button instanceof HTMLElement)) return;

    const liked = likedIds.has(this.productId);

    // The accessible name stays the same; aria-pressed carries the state.
    button.setAttribute('aria-pressed', String(liked));

    // Product page variant: visible label next to the heart on small screens.
    const text = button.querySelector('[data-wishlist-text]');
    if (text) text.textContent = liked ? config.strings.remove : config.strings.add;
    if (pending.has(this.productId)) button.setAttribute('aria-busy', 'true');
    else button.removeAttribute('aria-busy');
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (config.loggedIn) getToast();

// A page restored from the back/forward cache still has the hearts as they
// were when it was left. Apply the last saved list to it.
window.addEventListener('pageshow', (event) => {
  if (!event.persisted || !config.loggedIn || pending.size > 0) return;

  const recent = readRecent();
  if (!recent) return;

  const changed = new Set([...likedIds, ...recent]);
  likedIds.clear();
  recent.forEach((id) => likedIds.add(id));
  changed.forEach(notify);
});

// Header and menu-drawer links to the wishlist page.
document.addEventListener('click', (event) => {
  if (config.loggedIn) return;
  if (!(event.target instanceof Element) || !event.target.closest('[data-wishlist-link]')) return;

  event.preventDefault();
  openLogin();
});
